import type { ReasoningLevel } from '@awork/features/agent/models/agent-configuration.model'
import type { AgentConnector } from '@awork/features/agent/services/agent-service/types'
import type { AccessLevels } from '@awork/features/workspace/models/permissions.model'
import { apiEndpoint } from '@awork/environments/environment'
import type { AiTag } from '@awork/_shared/models/tag.model'

export interface IAgent {
  id: string
  createdBy?: string
  name: string
  description?: string
  type?: string
  skillIds?: string[]
  connectors?: AgentConnector[]
  currentSystemPromptVersionId?: string
  createdWithThreadId?: string
  presetKey?: string
  modelProvider?: string
  modelName?: string
  reasoningLevel?: ReasoningLevel
  systemPrompt?: string
  webSearchEnabled?: boolean
  aworkAccessEnabled?: boolean
  googleGenAiAccessEnabled?: boolean
  openAiImageGenerationAccessEnabled?: boolean
  isArchived?: boolean
  workspaceAccessLevel?: AccessLevels | null
  hasAdminAccessOnly?: boolean
  accessLevel?: AccessLevels
  createdOn?: string
  updatedOn?: string | Date
  hasImage?: boolean
  isOfficial?: boolean
  tags?: AiTag[]
  canEditTags?: boolean
  canSetOfficial?: boolean
}

/**
 * The AI agent model in awork.
 */
export class Agent implements IAgent {
  id: string
  createdBy?: string
  name: string
  description?: string
  type?: string
  skillIds?: string[]
  connectors?: AgentConnector[]
  currentSystemPromptVersionId?: string
  createdWithThreadId?: string
  presetKey?: string
  modelProvider?: string
  modelName?: string
  reasoningLevel?: ReasoningLevel
  systemPrompt?: string
  webSearchEnabled?: boolean
  aworkAccessEnabled?: boolean
  googleGenAiAccessEnabled?: boolean
  openAiImageGenerationAccessEnabled?: boolean
  isArchived?: boolean
  workspaceAccessLevel?: AccessLevels | null
  hasAdminAccessOnly?: boolean
  accessLevel?: AccessLevels
  createdOn?: string
  updatedOn?: string | Date
  hasImage?: boolean
  isOfficial: boolean
  tags: AiTag[]
  canEditTags: boolean
  canSetOfficial: boolean

  constructor(agent: Partial<IAgent>) {
    Object.assign(this, agent)
    this.isOfficial = agent.isOfficial ?? false
    this.tags = agent.tags ?? []
    this.canEditTags = agent.canEditTags ?? false
    this.canSetOfficial = agent.canSetOfficial ?? false
    this.connectors = agent.connectors?.map(connector => ({ ...connector, status: connector.status ?? 'pending' }))
  }

  /**
   * Returns the native files-service URL for this agent's avatar.
   * @param {number} [width=500] Requested image width.
   * @param {number} [height=500] Requested image height.
   * @returns {string} Agent avatar URL.
   */
  profileImage(width = 500, height = 500): string {
    const params = new URLSearchParams({
      crop: 'false',
      width: Math.floor(width).toString(),
      height: Math.floor(height).toString(),
      ...(this.updatedOn ? { v: new Date(this.updatedOn).getTime().toString() } : {})
    })
    return `${apiEndpoint}/files/images/agents/${this.id}?${params.toString()}`
  }
}
